import { asMove, ENGLISH_CROSS, parseBoard, replay } from "./board";
import {
  MAX_MOVES,
  type Move,
  type PegSolitaireDefinition,
  type PegSolitaireProgress,
  type PegSolitaireSubmission,
} from "./types";

export const DEFAULT_RULES = {
  en: "Jump a peg over a neighbouring peg, in a straight line along a row or a column, into the empty hole just behind it; the peg jumped over is removed. Clear the board until a single peg is left — in the marked hole, if there is one.",
  fr: "Faites sauter une fiche par-dessus une fiche voisine, en ligne droite sur une rangée ou une colonne, jusqu'au trou vide juste derrière ; la fiche sautée est retirée. Videz le plateau jusqu'à ce qu'il ne reste qu'une fiche — dans le trou marqué, s'il y en a un.",
};

/** The same starting board as the backend's `empty_definition()`. */
export function emptyDefinition(): PegSolitaireDefinition {
  return {
    puzzleType: "peg_solitaire",
    typeVersion: 1,
    board: [...ENGLISH_CROSS],
    target: [3, 3],
    rulesText: { ...DEFAULT_RULES },
  };
}

export function emptyProgress(_definition: PegSolitaireDefinition): PegSolitaireProgress {
  return { version: 1, moves: [], undone: [] };
}

/** Keeps the entries that are four integers, and no more of them than the schema allows. */
export function sanitizeMoves(raw: unknown): Move[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(asMove)
    .filter((move): move is Move => move !== null)
    .slice(0, MAX_MOVES);
}

/**
 * Accepts any stored blob (older versions, null, garbage) and returns a sequence this board can
 * actually play: a log recorded against another definition is cut at the first jump that no
 * longer works, rather than leaving the board in a position nobody can reach.
 */
export function migrateProgress(
  definition: PegSolitaireDefinition,
  raw: unknown,
): PegSolitaireProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  const source = raw as { moves?: unknown; undone?: unknown };
  const { board, pegs } = parseBoard(definition.board);
  const outcome = replay(board, pegs, sanitizeMoves(source.moves));
  return {
    version: 1,
    moves: outcome.moves,
    undone: outcome.illegalAt === null ? sanitizeMoves(source.undone) : [],
  };
}

/** The server replays the same jumps: what the player did, and nothing else. */
export function serializeSubmission(
  _definition: PegSolitaireDefinition,
  progress: PegSolitaireProgress,
): PegSolitaireSubmission {
  return { moves: progress.moves.map((move) => [...move] as Move) };
}
